import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, FileText, ChevronLeft, ChevronRight, Building2, CheckCircle, XCircle, Clock, Truck } from 'lucide-react';
import { comprasService } from '@/services/compras.service';
import { useAuthStore } from '@/stores/auth-store';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  DRAFT: { label: 'Borrador', color: 'bg-ios-gray-6 text-ios-gray', icon: FileText },
  PENDING: { label: 'Pendiente', color: 'bg-yellow-50 text-yellow-600', icon: Clock },
  APPROVED: { label: 'Aprobada', color: 'bg-ios-blue/10 text-ios-blue', icon: CheckCircle },
  RECEIVED: { label: 'Recibida', color: 'bg-ios-green/10 text-ios-green', icon: Truck },
  CANCELLED: { label: 'Cancelada', color: 'bg-red-50 text-ios-red', icon: XCircle },
};

export function PurchasesListPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { error, errorType, handleApiError, handleAuthRedirect } = useApiError();
  const [orders, setOrders] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const limit = 20;

  useEffect(() => {
    loadOrders();
  }, [search, statusFilter, page, user]);

  const loadOrders = async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    try {
      const result = await comprasService.findAll({
        companyId: user.company.id,
        search: search || undefined,
        status: statusFilter || undefined,
        page,
        limit,
      });
      setOrders(result.data || []);
      setTotal(result.total || 0);
      setTotalPages(result.totalPages || 1);
    } catch (err: any) { handleApiError(err); }
    setLoading(false);
  };

  return (
    <div className="w-full">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadOrders} onLogin={handleAuthRedirect} />
      {!error && (
      <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Órdenes de Compra</h1>
          <p className="text-sm text-ios-gray mt-1">{total} órdenes registradas</p>
        </div>
        <button onClick={() => navigate('/purchases/new')} className="ios-button flex items-center gap-2">
          <Plus size={18} />
          Nueva Orden
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Buscar folio o proveedor..."
            className="ios-input w-full pl-9 h-9 text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="ios-input h-9 text-sm"
        >
          <option value="">Todos los estados</option>
          <option value="DRAFT">Borrador</option>
          <option value="PENDING">Pendiente</option>
          <option value="APPROVED">Aprobada</option>
          <option value="RECEIVED">Recibida</option>
          <option value="CANCELLED">Cancelada</option>
        </select>
      </div>

      {/* Table */}
      <div className="ios-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Folio</th>
                <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Proveedor</th>
                <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Fecha</th>
                <th className="text-center px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Estado</th>
                <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Total</th>
                <th className="text-center px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Items</th>
                <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ios-gray-5">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="w-6 h-6 border-2 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-ios-gray text-sm">
                    No se encontraron órdenes de compra
                  </td>
                </tr>
              ) : (
                orders.map((order: any) => {
                  const config = statusConfig[order.status] || statusConfig.DRAFT;
                  const Icon = config.icon;
                  return (
                    <tr key={order.id} className="hover:bg-ios-gray-6/30 transition-colors cursor-pointer" onClick={() => navigate(`/purchases/${order.id}`)}>
                      <td className="px-4 py-3 font-medium">{order.folio}</td>
                      <td className="px-4 py-3">{order.supplier?.name || '-'}</td>
                      <td className="px-4 py-3 text-ios-gray text-xs">
                        {new Date(order.createdAt).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${config.color}`}>
                          <Icon size={10} />
                          {config.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">${Number(order.grandTotal).toFixed(2)}</td>
                      <td className="px-4 py-3 text-center text-ios-gray">
                        {order._count?.items || 0}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/purchases/${order.id}`); }}
                          className="text-xs text-ios-blue hover:underline font-medium"
                        >
                          Ver
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-ios-gray-5">
            <span className="text-xs text-ios-gray">Página {page} de {totalPages}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg hover:bg-ios-gray-6 disabled:opacity-50">
                <ChevronLeft size={16} />
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg hover:bg-ios-gray-6 disabled:opacity-50">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}
